import React, { useEffect, useState } from 'react';
import { useLocation, useRouteMatch } from 'react-router-dom';
import queryString from 'query-string';
import { CCSRFToken, CSAUTHID, CSAUTHToken, CSTEMPUSERID } from 'libs/api/apiClient';
import { handleFieldError, moveToFirstError } from 'libs/api/errorHandle';
import { userKakaoLogin, userLogin } from 'libs/api/user';
import { IKakaoLoginInfo, TUserLogin } from 'modules/user';
import { Button, Form, Input, Row, Col, Typography } from 'antd';
import styled from 'styled-components';
import { FormWrapper, device } from 'GlobalStyles';
import LoginBackgroundSource from 'assets/images/banners/login.webp';
import TenantSource_LG from 'assets/images/logos/lg.svg';
import TenantSource_DN from 'assets/images/logos/dn.png';
import LogoSource from 'assets/images/logos/logo.svg';
import {isMobile} from 'react-device-detect';
import { showAlert } from 'components/modal/showConfirm';
import { RouteMatch } from 'modules/types';
import { ERoute } from 'enums/route.enum';

const { Title, Text } = Typography;

const KaitusLogoImage = styled.img`
  width: 150px;
  margin-right: 50px;
  cursor: pointer;

  @media screen and ${device.md} {
    width: 133px;
    margin-right: 20px;
  }
`;

export const LogoImage = (source: string) => {
  return <KaitusLogoImage alt='logo' src={source} />;
};

const Login = () => {
  useEffect(() => {
    if (isMobile) {
      return showAlert(undefined, 'PC로만 assessment가 가능합니다');
    }
  }, []);

  const [form] = Form.useForm();
  const next = queryString.parse(useLocation().search)?.next?.toString();
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);

  const baseUrl = ERoute.KaitusLogin
  const match = useRouteMatch(baseUrl);
  const { tenant_id } = (match?.params as RouteMatch) || {};

  const onClickLogin = () => {
    if (isMobile) {
      return showAlert(undefined, 'PC로만 assessment가 가능합니다');
    }
    
    form
      .validateFields()
      .then((value) => {
        handleLogin(value as TUserLogin);
      })
      .catch((errorInfo) => {
        moveToFirstError(form, errorInfo.errorFields);
      });
  };
  const handleLogin = async (value: TUserLogin) => {
    try {
      setSubmitButtonDisabled(true);
      const response = await userLogin({
        userid: value.userid,
        password: value.password,
        is_remember: value.is_remember,
      });
      const authorization = response.headers.authorization.split('Basic ')[1];
      window.localStorage.setItem(CCSRFToken, authorization);
      window.location.href = next ? next : '/';
    } catch (e) {
      handleFieldError(e, form);
      throw e;
    } finally {
      setSubmitButtonDisabled(false);
    }
  };
  const onClickKakaoLogin = () => {
    handleKakaoLogin();
  };
  const handleKakaoLogin = async () => {
    try {
      setSubmitButtonDisabled(true);
      if (!Kakao.isInitialized()) {
        console.log('kakao key => ', process.env.REACT_APP_KAKAO_KEY);
        Kakao.init(process.env.REACT_APP_KAKAO_KEY ?? 'cda75f3cd4112396004aca5d7da58bc8');
      }
      Kakao.Auth.login({
        success: async (authObj: Kakao.Auth.AuthSuccessObject): Promise<void> => {
          console.log(`kakao token => ${authObj.access_token}`);
          const kakaoAccessToken: IKakaoLoginInfo = {
            kakao_access_token: authObj.access_token,
          };
          const response = await userKakaoLogin({
            type: 'kakao',
            token: `Bearer ${kakaoAccessToken.kakao_access_token!}`,
            code: 'web',
            userid: 'dummy@dummy.com',
            password: 'dummy',
            country_code: '82', // TODO::
          });
          console.log('response status => ', response.status);
          if (response.status === 203) {
            const authid = response.data.id;
            const userid = response.data.email;
            window.localStorage.setItem(CSAUTHID, authid);
            window.localStorage.setItem(CSTEMPUSERID, userid);
            window.localStorage.setItem(CSAUTHToken, `Bearer ${kakaoAccessToken.kakao_access_token!}`);
            window.location.href = '/user/signup?type=kakao';
          } else {
            console.log('response => ', response.data.token);
            const authorization = response.headers.authorization.split('Basic ')[1];
            window.localStorage.setItem(CCSRFToken, authorization);
            window.location.href = next ? next : '/';
          }
          setSubmitButtonDisabled(false);
        },
        fail: (err: Kakao.Auth.AuthError): void => {
          console.log('fail', err);
          setSubmitButtonDisabled(false);
        },
      });
    } catch (e) {
      handleFieldError(e, form);
      throw e;
    } finally {
      setSubmitButtonDisabled(false);
    }
  };

  const appleResponse = async (response: any) => {
    console.log(response);
    if (!response.error) {
      const code = response.authorization.code;
      const id_token = response.authorization.id_token;
      //console.log(`code ==> ${code}`)
      //console.log(`id_token ==> ${id_token}`);

      try {
        setSubmitButtonDisabled(true);

        const response = await userKakaoLogin({
          type: 'apple',
          token: id_token,
          code: 'web',
          userid: 'dummy@dummy.com',
          password: 'dummy',
          country_code: '82', // TODO::
        });
        console.log('response status => ', response.status);
        if (response.status === 203) {
          const authid = response.data.id;
          const userid = response.data.email;
          window.localStorage.setItem(CSAUTHID, authid);
          window.localStorage.setItem(CSTEMPUSERID, userid);
          window.localStorage.setItem(CSAUTHToken, id_token);
          window.location.href = '/user/signup?type=apple';
        } else {
          console.log('response => ', response.data.token);
          const authorization = response.headers.authorization.split('Basic ')[1];
          window.localStorage.setItem(CCSRFToken, authorization);
          window.location.href = next ? next : '/';
        }
      } catch (e) {
        handleFieldError(e, form);
        throw e;
      } finally {
        setSubmitButtonDisabled(false);
      }
    }
  };

  if (tenant_id === 'dn') {
    return (
      <Row style={{ maxWidth: '1440px', margin: '0 auto' }}>
        <Col xs={0} sm={0} md={0} lg={12} xl={12}>
          <img alt='' src={LoginBackgroundSource} style={{ width: '100%', height: '100%' }} />
        </Col>
        <Col xs={24} sm={24} md={24} lg={12} xl={12}>
          <FormWrapper style={{ maxWidth: '600px' }}>
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            <Row>
              <Col className='logo-image'>{LogoImage(LogoSource)}</Col>
              <Col style={{ marginTop: '3px' }} className='logo-image'>{LogoImage(TenantSource_DN)}</Col>
            </Row>
            <br />
            <br />
            <br />
            <Title level={2}>로그인</Title>
            <Text style={{ marginTop: '30px', marginBottom: '20px', display: 'inline-block' }}>
            STAR 과정 (Strategic Thinking Approach & Reinforcement)의 Assessment 페이지 입니다.
            </Text>
            <Text style={{ marginBottom: '20px', display: 'inline-block' }}>
            미리 안내받은 ID와 PW로 접속해주시기 바랍니다.
            </Text>
            <Text style={{ marginBottom: '20px', display: 'inline-block' }}>
            ID, PW를 못 받았거나 확인이 필요하신 분은 아래 담당자에게 문의해 주세요. (운영시간: 06시~24시)
            </Text>
            <Text style={{ marginBottom: '0px', display: 'inline-block' }}>
            #1. 수신처: yun.sangmoon@bigstepedu.com
            </Text>
            <br />
            <Text style={{ marginBottom: '100px', display: 'inline-block' }}>
            #2. 수신처: moon.heesung@bigstepedu.com
            </Text>
            <Form
              form={form}
              name='login'
              layout='vertical'
              className='form-inline'
              initialValues={{
                is_remember: true,
              }}
            >
              <Form.Item
                name={'userid'}
                className='form-item'
                label='아이디'
                rules={[
                  {
                    type: 'email',
                    message: '이메일 형식이 올바르지 않습니다',
                  },
                  { required: true },
                ]}
              >
                <Input size='large' placeholder='이메일을 입력해주세요' />
              </Form.Item>
              <Form.Item name={'password'} className='form-item' label='비밀번호' rules={[{ required: true }]}>
                <Input.Password size='large' type='password' placeholder='비밀번호를 입력해주세요' />
              </Form.Item>
              <Form.Item shouldUpdate={true} className='form-button'>
                {() => (
                  <Button
                    type='primary'
                    htmlType='submit'
                    size='large'
                    onClick={onClickLogin}
                    loading={submitButtonDisabled}
                  >
                    로그인
                  </Button>
                )}
              </Form.Item>
            </Form>
          </FormWrapper>
        </Col>
      </Row>
    );
    } else if (tenant_id === 'lg') {
      return (
        <Row style={{ maxWidth: '1440px', margin: '0 auto' }}>
          <Col xs={0} sm={0} md={0} lg={12} xl={12}>
            <img alt='' src={LoginBackgroundSource} style={{ width: '100%', height: '100%' }} />
          </Col>
          <Col xs={24} sm={24} md={24} lg={12} xl={12}>
            <FormWrapper style={{ maxWidth: '600px' }}>
              <br />
              <br />
              <br />
              <br />
              <br />
              <br />
              <Row>
                <Col className='logo-image'>{LogoImage(LogoSource)}</Col>
                <Col className='logo-image'>{LogoImage(TenantSource_LG)}</Col>
              </Row>
              <br />
              <br />
              <br />
              <Title level={2}>로그인</Title>
              <Text style={{ marginTop: '30px', marginBottom: '20px', display: 'inline-block' }}>
              [미래사업가 Essential] 프로그램의 Assessment 페이지 입니다.
              </Text>
              <Text style={{ marginBottom: '20px', display: 'inline-block' }}>
              인화원에서 안내 받은 ID와 PW로 접속해주시기 바랍니다.
              </Text>
              <Text style={{ marginBottom: '20px', display: 'inline-block' }}>
              ID, PW를 못 받았거나 확인이 필요하신 분은 아래 담당자에게 문의해주십시오.
              </Text>
              <Text style={{ marginBottom: '0px', display: 'inline-block' }}>
              #1. '미래사업가 Essential' 슬랙에서 DM을 보내주세요. → 수신처: (CS운영진)
              </Text>
              <br />
              <Text style={{ marginBottom: '100px', display: 'inline-block' }}>
              #2. 슬랙이 원활하지 않으시면 메일을 주셔도 됩니다. → 수신처: yun.sangmoon@bigstepedu.com
              </Text>
              <Form
                form={form}
                name='login'
                layout='vertical'
                className='form-inline'
                initialValues={{
                  is_remember: true,
                }}
              >
                <Form.Item
                  name={'userid'}
                  className='form-item'
                  label='아이디'
                  rules={[
                    {
                      type: 'email',
                      message: '이메일 형식이 올바르지 않습니다',
                    },
                    { required: true },
                  ]}
                >
                  <Input size='large' placeholder='이메일을 입력해주세요' />
                </Form.Item>
                <Form.Item name={'password'} className='form-item' label='비밀번호' rules={[{ required: true }]}>
                  <Input.Password size='large' type='password' placeholder='비밀번호를 입력해주세요' />
                </Form.Item>
                <Form.Item shouldUpdate={true} className='form-button'>
                  {() => (
                    <Button
                      type='primary'
                      htmlType='submit'
                      size='large'
                      onClick={onClickLogin}
                      loading={submitButtonDisabled}
                    >
                      로그인
                    </Button>
                  )}
                </Form.Item>
              </Form>
            </FormWrapper>
          </Col>
        </Row>
      );
    } 
    else {
      return (
        <div></div>
      )
    }
};

export default Login;
